<script setup lang="ts">
import { computed } from "vue";
import PhosphorIcon from "./PhosphorIcon.vue";

const props = withDefaults(
  defineProps<{
    icon?: string;
    phosphorIcon?: string;
    phosphorWeight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
    rightIcon?: string;
    label?: string;
    disabled?: boolean;
    loading?: boolean;
    variant?:
      | "primary"
      | "secondary"
      | "superadmin"
      | "success"
      | "danger"
      | "ia"
      | "toolbar"
      | "flat"
      | "flat-danger"
      | "outline"
      | "outline-danger"
      | "outline-ia"
      | "outline-white";

    size?: "xs" | "sm" | "lg";
    submit?: boolean;
    iconSize?: string;
  }>(),
  {
    variant: "primary",
    submit: false,
    size: "sm",
  }
);

const iconSizes = {
  xs: "14px",
  sm: "16px",
  lg: "20px",
};

const fontSizes = {
  xs: "12px",
  sm: "14px",
  lg: "14px",
};

const iconSize = computed(() => {
  if (props.iconSize) return props.iconSize;
  return iconSizes[props.size || "sm"];
});
</script>

<template>
  <button
    v-bind="$attrs"
    :type="submit ? 'submit' : 'button'"
    :class="{
      'btn btn-dark': variant === 'secondary',
      'btn btn-primary text-white': variant === 'primary',
      'btn btn-danger text-white': variant === 'danger',
      'btn btn-warning text-dark': variant === 'superadmin',
      'btn btn-success text-white': variant === 'success',
      'btn btn-ia text-white': variant === 'ia',
      'btn btn-toolbar text-white toolbar ': variant === 'toolbar',
      'btn btn-outline-dark outline ': variant === 'outline',
      'btn btn-flat ': variant === 'flat',
      'btn btn-flat-danger ': variant === 'flat-danger',
      'btn btn-outline-danger outline outline-danger ':
        variant === 'outline-danger',
      'btn btn-outline-dark outline outline-ia ': variant === 'outline-ia',
      'btn btn-outline-white outline  outline-white ':
        variant === 'outline-white',
      'btn-size-sm': size === 'sm',
      'btn-lg': size === 'lg',
      'btn-size-xs': size === 'xs',
    }"
    :disabled="disabled || loading"
  >
    <div
      class="d-flex flex-nowrap gap-3 align-items-center justify-content-center"
    >
      <template v-if="phosphorIcon || icon || variant === 'ia'">
        <span
          v-if="loading"
          class="spinner-border"
          :class="{
            ' spinner-border-sm': size === 'sm' || size === 'xs',
            ' spinner-border-lg': size === 'lg',
          }"
          role="status"
          aria-hidden="true"
        ></span>
        <PhosphorIcon
          v-else-if="phosphorIcon"
          :name="phosphorIcon"
          :size="parseInt(iconSizes[size])"
          :weight="phosphorWeight ?? 'regular'"
        />
        <i
          v-else
          :class="variant === 'ia' ? icon ?? 'bi-stars' : icon"
          :style="{
            fontSize: iconSize,
          }"
        ></i>
      </template>

      <span
        v-if="label"
        :style="{
          fontSize: fontSizes[size],
        }"
        >{{ label }}</span
      >
      <i
        v-if="rightIcon"
        :class="rightIcon"
        :style="{
          fontSize: iconSize,
        }"
      ></i>
    </div>
  </button>
</template>

<style lang="scss" scoped>
.outline {
  background-color: transparent;
  transition: background-color 0.2s ease, color 0.2s ease;
  color: var(--bs-dark);
  border: 2px solid var(--bs-dark-opacity-10);
  &:hover {
    color: var(--bs-white);
    background-color: var(--bs-dark);
  }
}

.outline-danger {
  border: 2px solid var(--bs-danger-opacity-10);
  color: var(--bs-danger);
  &:hover {
    background-color: var(--bs-danger);
  }
}

.outline-white {
  border: 2px solid rgba(var(--bs-white), 0.1);
  color: var(--bs-white);
  &:hover {
    background-color: var(--bs-white);
    color: var(--bs-dark);
  }
}
.outline-white.btn:disabled {
  border: 2px solid rgba(var(--bs-white), 0.1);
  color: var(--bs-white);
  opacity: 0.5;
}
// En thème sombre, le bouton prend un fond --bs-white (cf. `.outline`) : le
// libellé désactivé en --bs-white serait de la couleur du fond, donc invisible.
[data-bs-theme="dark"] .outline-white.btn:disabled {
  color: var(--bs-dark);
}

.outline-ia {
  border: 2px solid color-mix(in srgb, var(--bs-ia), #ffffff00);
  color: var(--bs-ia);
  &:hover {
    background-color: var(--bs-ia);
    border: 2px solid var(--bs-ia);
  }
}

.btn-size-sm {
  padding: 0.25rem 0.8rem;
  font-size: 0.875rem;
  line-height: 1.5;
}

.btn-ia {
  background: linear-gradient(135deg, #8b5cf6 0%, var(--bs-ia) 100%);
  color: white !important;
  border: none;
  &:hover {
    background: linear-gradient(
      135deg,
      #7c4ddb 0%,
      color-mix(in srgb, var(--bs-ia), black 15%) 100%
    );
  }

  i {
    animation: blink-slow 2s ease-in-out infinite;
  }
}

@keyframes blink-slow {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.btn-size-xs {
  padding: 0.125rem 0.8rem;
  font-size: 0.75rem;
  line-height: 1.5;
}

.btn-primary {
  background-color: var(--bs-primary);
  color: white !important;
  &:hover {
    background-color: color-mix(in srgb, var(--bs-primary), black 10%);
  }
}

[data-bs-theme="dark"] {
  .outline {
    background-color: var(--bs-white);
    border: 2px solid var(--bs-white);
    color: var(--bs-dark);
    &:hover {
      color: var(--bs-white);
      background-color: var(--bs-dark);
      border: 2px solid var(--bs-dark);
    }
  }

  .outline-danger {
    background-color: var(--bs-danger);
    border: 2px solid var(--bs-danger);
    color: var(--bs-dark) !important;
    transition: all 0.2s ease-in-out;
    &:hover {
      color: var(--bs-white) !important;
      background-color: var(--bs-dark);
      border: 2px solid var(--bs-dark);
    }
  }
}

.btn-flat {
  background-color: transparent;
  border: none;
  box-shadow: none;
  color: var(--bs-dark);
  &:hover {
    background-color: var(--bs-dark-opacity-10);
  }
}
.btn-flat-danger {
  background-color: transparent;
  border: none;
  box-shadow: none;
  color: var(--bs-danger);
  &:hover {
    background-color: var(--bs-danger-opacity-10);
  }
}
</style>
